// src/components/AdminProjectTeam.jsx
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import projectCatalog from '../Config/projectCatalog';
import API_BASE_URL from '../Config/config';
import { 
 UserGroupIcon, 
 UserIcon, 
 PlusIcon, 
 CheckCircleIcon,
 ArrowPathIcon,
 ClipboardDocumentListIcon,
 TrashIcon,
 XMarkIcon,
 EyeIcon,
 ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const API = API_BASE_URL;

// Helper function to get admin headers with user_id
const getAdminHeaders = () => {
 const userId = localStorage.getItem('user_id');
 if (!userId) {
 throw new Error('User ID not found. Please login again.');
 }
 return {
 'Content-Type': 'application/json',
 'user-id': userId
 };
};

// Normalize experience level and role to match projectCatalog format exactly
// This ensures consistent matching between bootcamp enrollment and admin project assignment
const normalizeLevel = (lvl) => String(lvl || '').trim();
const normalizeRole = (r) => String(r || '').trim();

const AdminProjectTeam = () => {
 const [users, setUsers] = useState([]);
 const [teams, setTeams] = useState([]);
 const [assignedUsers, setAssignedUsers] = useState([]);
 const [selectedUsers, setSelectedUsers] = useState([]);
 const [level, setLevel] = useState('');
 const [role, setRole] = useState('');
 const [selectedProjectName, setSelectedProjectName] = useState('');
 const [activeTab, setActiveTab] = useState('team'); // 'team' or 'individual'
 const [filteredUsers, setFilteredUsers] = useState([]);
 const [teamName, setTeamName] = useState('');
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [creatingTeam, setCreatingTeam] = useState(false);
 const [assigningProject, setAssigningProject] = useState(false);
 const [unassigningUser, setUnassigningUser] = useState(null);
 const [deletingProject, setDeletingProject] = useState(null);
 const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
 const [showUnassignConfirm, setShowUnassignConfirm] = useState(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [showStats, setShowStats] = useState(true);

 // Check if user has admin role (admin access control)
 const currentUserId = localStorage.getItem('user_id');
 const user = JSON.parse(localStorage.getItem('user') || '{}');
 const isAdmin = user?.role === 'admin';

 const fetchUsers = useCallback(async () => {
 try {
 setError(null);
 console.log('🔍 Fetching users from:', `${API}/api/admin-teams/all-users`);
 const res = await axios.get(`${API}/api/admin-teams/all-users`, {
 headers: getAdminHeaders()
 });
 console.log('📦 Raw API response:', res);
 console.log('📦 Response data:', res.data);
 console.log('📦 Response status:', res.status);
 
 // Ensure userData is always an array
 // Lambda response format: { ok: true, data: [...] } or direct array
 let userData = res.data || [];
 
 // Handle Lambda wrapper format: { ok: true, data: [...] }
 if (res.data && res.data.ok && Array.isArray(res.data.data)) {
 userData = res.data.data;
 }
 // Handle direct array response
 else if (Array.isArray(res.data)) {
 userData = res.data;
 }
 // Handle other wrapped formats
 else if (!Array.isArray(userData)) {
 if (Array.isArray(res.data?.users)) {
 userData = res.data.users;
 } else if (res.data && typeof res.data === 'object') {
 // Check if it's an object with numeric keys (spread array issue)
 const keys = Object.keys(res.data);
 if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k)))) {
 // This is a spread array object like { 0: user1, 1: user2, ... }
 console.warn('⚠️ Received spread array format, converting back to array');
 userData = keys.map(k => res.data[k]).filter(Boolean);
 } else {
 console.warn('⚠️ Unexpected users data format:', res.data);
 userData = [];
 }
 } else {
 console.warn('⚠️ Unexpected users data format:', res.data);
 userData = [];
 }
 }
 console.log('✅ Fetched users:', Array.isArray(userData) ? userData.length : 0, userData);
 if (userData.length === 0) {
 console.warn('⚠️ No users found. This could mean:');
 console.warn(' 1. No users are enrolled in bootcamp_enrollments table');
 console.warn(' 2. API endpoint returned empty array');
 console.warn(' 3. Authentication/authorization issue');
 }
 setUsers(Array.isArray(userData) ? userData : []);
 } catch (err) {
 console.error('❌ Error fetching users:', err);
 console.error('❌ Error details:', {
 message: err.message,
 response: err.response?.data,
 status: err.response?.status,
 statusText: err.response?.statusText,
 config: {
 url: err.config?.url,
 headers: err.config?.headers
 }
 });
 setError(`Failed to fetch users: ${err.response?.data?.message || err.message || 'Unknown error'}`);
 setUsers([]);
 }
 }, []);

 const fetchTeams = useCallback(async () => {
 try {
 const res = await axios.get(`${API}/api/admin-teams/all-teams`, {
 headers: getAdminHeaders()
 });
 const teamData = Array.isArray(res.data) ? res.data : [];
 console.log('Fetched teams:', teamData.length, teamData);
 setTeams(teamData);
 } catch (err) {
 console.error('Error fetching teams:', err);
 setError('Failed to fetch teams');
 setTeams([]);
 }
 }, []);

 const fetchAssignedUsers = useCallback(async () => {
 try {
 const res = await axios.get(`${API}/api/admin-projects/assigned-users`, {
 headers: getAdminHeaders()
 });
 const raw = res.data;

 // Normalize response into an array in all cases
 let assignedData = [];
 if (Array.isArray(raw)) {
 assignedData = raw;
 } else if (Array.isArray(raw?.data)) {
 assignedData = raw.data;
 } else if (Array.isArray(raw?.items)) {
 assignedData = raw.items;
 } else if (raw && typeof raw === 'object') {
 // Some backends wrap arrays in { ok: true, results: [...] } or similar
 const possibleArrayKey = Object.keys(raw).find(
 (k) => Array.isArray(raw[k])
 );
 if (possibleArrayKey) {
 assignedData = raw[possibleArrayKey];
 }
 }

 console.log(
 'Fetched assigned users:',
 Array.isArray(assignedData) ? assignedData.length : 'n/a',
 assignedData
 );
 setAssignedUsers(assignedData);
 } catch (err) {
 console.error('Error fetching assigned users:', err);
 setError('Failed to fetch assigned users');
 setAssignedUsers([]);
 }
 }, []);

 const fetchAll = useCallback(async () => {
 setLoading(true);
 try {
 await Promise.all([fetchUsers(), fetchTeams(), fetchAssignedUsers()]);
 } catch (err) {
 console.error('Error fetching data:', err);
 setError('Failed to fetch data');
 } finally {
 setLoading(false);
 }
 }, [fetchUsers, fetchTeams, fetchAssignedUsers]);

 useEffect(() => {
 console.log('🚀 AdminProjectTeam mounted. API Base URL:', API);
 console.log('🚀 Current user:', { user_id: currentUserId, isAdmin, user });
 fetchAll();
 }, [fetchAll]);

 useEffect(() => {
 // Filter users by experience level, role, and work_mode
 // Show ALL users from enrollment table, but log which ones are missing required fields
 const allUsers = Array.isArray(users) ? users : [];
 
 // Log all users and their fields for debugging
 console.log('👥 All users from API:', {
 total: allUsers.length,
 users: allUsers.map(u => ({
 user_id: u.user_id,
 name: u.name,
 role: u.role,
 experience_level: u.experience_level,
 work_mode: u.work_mode,
 track: u.track,
 team_id: u.team_id
 }))
 });
 
 // Find users missing required fields
 const usersMissingFields = allUsers.filter(user => 
 user && !(
 // Has both role and experience_level
 (user.role !== null && 
 user.role !== undefined && 
 user.role !== '' &&
 user.experience_level !== null &&
 user.experience_level !== undefined &&
 user.experience_level !== '')
 ||
 // OR is on roles track
 (user.track === 'roles')
 )
 );

 if (usersMissingFields.length > 0) {
 console.warn('⚠️ Users missing required fields (role/experience_level):', {
 count: usersMissingFields.length,
 users: usersMissingFields.map(u => ({
 user_id: u.user_id,
 name: u.name,
 role: u.role || 'MISSING',
 experience_level: u.experience_level || 'MISSING',
 track: u.track || 'MISSING',
 work_mode: u.work_mode
 }))
 });
 }

 // Show ALL users, not just those with role/experience_level
 // This allows admins to see all enrolled users and assign them to teams
 // Users without role/experience_level can still be assigned manually
 const validUsers = allUsers.filter(user => user != null);

 // Debug logging
 console.log(`🔍 ${activeTab === 'team' ? 'Team' : 'Individual'} Tab - Users analysis:`, {
 totalUsers: users.length,
 validUsers: validUsers.length,
 matchingWorkMode: validUsers.filter(u => {
 if (activeTab === 'team') {
 return u.work_mode === 'team' || u.work_mode === 'teams';
 } else {
 return u.work_mode === 'single' || u.work_mode === 'individual';
 }
 }).length,
 usersByWorkMode: {
 team: validUsers.filter(u => u.work_mode === 'team' || u.work_mode === 'teams').length,
 single: validUsers.filter(u => u.work_mode === 'single').length,
 individual: validUsers.filter(u => u.work_mode === 'individual').length,
 other: validUsers.filter(u => !['team', 'teams', 'single', 'individual'].includes(u.work_mode)).length
 }
 });

 // Helper function to check if user matches the active tab based on work_mode
 // Team Management tab: Only show users with work_mode='team' or 'teams'
 // Individual Assignment tab: Only show users with work_mode='single' or 'individual'
 const matchesTab = (user) => {
 if (activeTab === 'team') {
 // Team Management: Only show users who have chosen team work mode
 return user.work_mode === 'team' || user.work_mode === 'teams';
 } else {
 // Individual Assignment: Only show users who have chosen individual work mode
 return user.work_mode === 'single' || user.work_mode === 'individual';
 }
 };

 // Filter by track and work_mode
 // ADMIN VIEW: Show ALL users - admins need to see everyone to assign them
 let filtered = validUsers.filter(user => 
 matchesTab(user) // This now returns true for all users
 );

 // Normalize filters for comparison
 const normalizedFilterLevel = normalizeLevel(level);
 const normalizedFilterRole = normalizeRole(role);
 
 // IMPORTANT: When NO filters are selected, show ALL users
 // This allows admins to see all enrolled users and assign them to teams/projects
 // Only apply filters if BOTH level AND role are explicitly selected
 if (normalizedFilterLevel && normalizedFilterRole) {
 // Both filters selected - show users matching filters OR users without complete data
 filtered = filtered.filter(user => {
 const userLevel = normalizeLevel(user.experience_level);
 const userRole = normalizeRole(user.role);
 const hasRole = user.role !== null && user.role !== undefined && user.role !== '';
 const hasLevel = user.experience_level !== null && user.experience_level !== undefined && user.experience_level !== '';
 
 // Include if matches filters OR if user doesn't have role/level set (for manual assignment)
 return (userLevel === normalizedFilterLevel && userRole === normalizedFilterRole) ||
 (!hasRole || !hasLevel); // Show users without complete data for manual assignment
 });
 } else if (normalizedFilterLevel) {
 // Only level filter selected - show users with that level OR users without level
 filtered = filtered.filter(user => {
 const userLevel = normalizeLevel(user.experience_level);
 const hasLevel = user.experience_level !== null && user.experience_level !== undefined && user.experience_level !== '';
 return userLevel === normalizedFilterLevel || !hasLevel;
 });
 } else if (normalizedFilterRole) {
 // Only role filter selected - show users with that role OR users without role
 filtered = filtered.filter(user => {
 const userRole = normalizeRole(user.role);
 const hasRole = user.role !== null && user.role !== undefined && user.role !== '';
 return userRole === normalizedFilterRole || !hasRole;
 });
 }
 // If NO filters are selected (normalizedFilterLevel and normalizedFilterRole are both empty),
 // show all users matching the active tab's work_mode (team/teams for team tab, single/individual for individual tab)

 console.log('📊 Final filtered users result:', {
 activeTab,
 levelFilter: level || 'None',
 roleFilter: role || 'None',
 totalUsersFromAPI: allUsers.length,
 validUsersAfterValidation: validUsers.length,
 filteredCount: filtered.length,
 usersInTeams: filtered.filter(u => u.team_id).length,
 availableUsers: filtered.filter(u => !u.team_id).length,
 sampleUsers: filtered.slice(0, 5).map(u => ({ 
 user_id: u.user_id, 
 name: u.name,
 role: u.role || 'MISSING', 
 experience_level: u.experience_level || 'MISSING', 
 work_mode: u.work_mode || 'NULL',
 track: u.track || 'NULL',
 team_id: u.team_id || 'None'
 }))
 });

 setFilteredUsers(filtered);
 }, [users, level, role, activeTab]);

 // Access control check - after all hooks
 if (!isAdmin) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
 <div className="text-center">
 <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
 <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
 <p className="text-muted text-lg mb-6">
 Admin access required. Only users with admin role can access this functionality.
 </p>
 <p className="text-muted text-sm">
 Current user: {currentUserId || 'Not logged in'}
 </p>
 </div>
 </div>
 );
 }

 const handleSelect = (user) => {
 // Don't allow selecting users already in a team
 if (user.team_id) {
 return;
 }
 const exists = selectedUsers.some((u) => u.user_id === user.user_id);
 if (exists) setSelectedUsers(selectedUsers.filter((u) => u.user_id !== user.user_id));
 else if (selectedUsers.length < 4) setSelectedUsers([...selectedUsers, user]);
 };

 const createTeam = async (isRandom) => {
 // Validate required fields - only team name is required now
 if (!teamName.trim()) {
 alert('⚠️ Please enter a team name.');
 return;
 }
 
 // Filter out users already in teams for team creation
 const availableUsers = filteredUsers.filter(u => !u.team_id);
 
 if (isRandom) {
 // For random team, check if we have enough available users
 if (availableUsers.length < 4) {
 alert(`⚠️ Not enough users available. Need at least 4 users who are not already in a team.\n\nAvailable: ${availableUsers.length}\n\nPlease wait for more users to enroll or clear filters.`);
 return;
 }
 } else {
 // For manual team, check if exactly 4 users are selected
 const validSelectedUsers = selectedUsers.filter(u => !u.team_id);
 if (validSelectedUsers.length !== 4) {
 alert(`⚠️ Please select exactly 4 users who are not already in a team.\n\nCurrently selected: ${validSelectedUsers.length}/4`);
 return;
 }
 }

 const list = isRandom ? 
 [...availableUsers].sort(() => 0.5 - Math.random()).slice(0, 4) : 
 selectedUsers.filter(u => !u.team_id); // Ensure selected users are not in teams
 
 if (list.length !== 4) {
 alert('⚠️ A team must have exactly 4 members.');
 return;
 }

 setCreatingTeam(true);
 try {
 // Get level and role from first user (or use filters if available, otherwise null)
 const firstUser = list[0];
 const teamLevel = level || firstUser?.experience_level || null;
 const teamRole = role || firstUser?.role || null;

 const payload = {
 team_name: teamName.trim(),
 members: list.map(u => u.user_id),
 level: teamLevel,
 role: teamRole,
 project: null // Don't assign project during creation
 };
 
 console.log('🚀 Creating team with payload:', payload);
 
 const response = await axios.post(`${API}/api/admin-teams/create-team`, payload, {
 headers: getAdminHeaders()
 });
 
 console.log('✅ Team creation response:', response.data);
 
 const createdTeamId = response.data?.team_id || response.data?.data?.team_id;
 
 if (!createdTeamId) {
 throw new Error('Team created but no team_id returned');
 }

 // Get repo URL from team creation response (backend creates repo automatically)
 const teamRepoUrl = response.data?.repo_url || response.data?.data?.repo_url;
 
 // If a project is selected, assign it to the team
 let projectRepoUrl = null;
 if (selectedProjectName) {
 try {
 console.log('📦 Assigning project to team:', selectedProjectName);
 const assignResponse = await assignToTeam(createdTeamId);
 projectRepoUrl = assignResponse?.repo_url;
 console.log('✅ Project assigned successfully, repo URL:', projectRepoUrl);
 } catch (assignErr) {
 console.error('⚠️ Team created but failed to assign project:', assignErr);
 // Don't show alert here, we'll show it in the final message
 }
 }
 
 await fetchAll();
 setSelectedUsers([]);
 setTeamName('');
 
 // Show success message with GitHub repo URL
 const repoUrl = projectRepoUrl || teamRepoUrl;
 if (selectedProjectName && repoUrl) {
 const openRepo = window.confirm(
 '✅ Team created successfully!\n✅ Project assigned!\n✅ GitHub repository created!\n✅ All team members added as collaborators!\n\n' +
 `Repository: ${repoUrl}\n\nWould you like to open the GitHub repository?`
 );
 if (openRepo) {
 window.open(repoUrl, '_blank');
 }
 } else if (repoUrl) {
 const openRepo = window.confirm(
 '✅ Team created successfully!\n✅ GitHub repository created!\n✅ All team members added as collaborators!\n\n' +
 `Repository: ${repoUrl}\n\nWould you like to open the GitHub repository?`
 );
 if (openRepo) {
 window.open(repoUrl, '_blank');
 }
 } else if (selectedProjectName) {
 alert('✅ Team created successfully!\n✅ Project assigned!\n✅ GitHub collaborators setup complete!');
 } else {
 alert('✅ Team created successfully!\n✅ GitHub repository created!\n✅ All team members added as collaborators!\n\n💡 Tip: You can assign a project to this team from the"Existing Teams" section.');
 }
 } catch (err) {
 console.error('❌ Error creating team:', err);
 console.error('❌ Error response:', err.response);
 const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
 alert(`❌ Failed to create team:\n\n${errorMessage}\n\nPlease check the console for more details.`);
 } finally {
 setCreatingTeam(false);
 }
 };

 const addCollaborators = async (teamMembers) => {
 try {
 // Add each team member as collaborator to all other team members
 for (let i = 0; i < teamMembers.length; i++) {
 for (let j = 0; j < teamMembers.length; j++) {
 if (i !== j) {
 await axios.post(`${API}/api/admin-teams/add-collaborator`, {
 user_id: teamMembers[i].user_id,
 collaborator_id: teamMembers[j].user_id
 }, {
 headers: getAdminHeaders()
 });
 }
 }
 }
 } catch (err) {
 console.error('Failed to add collaborators:', err);
 }
 };

 const assignToUser = async (userId) => {
 if (!selectedProjectName) {
 alert('Please select a project.');
 return;
 }
 if (!level || !role) {
 alert('Please select both experience level and role first.');
 return;
 }
 
 setAssigningProject(true);
 try {
 await axios.post(`${API}/api/admin-projects/assign-user-project`, {
 user_id: userId,
 project_name: selectedProjectName,
 level,
 role,
 create_repo: true
 }, {
 headers: getAdminHeaders()
 });
 alert('✅ Project assigned to user successfully!');
 fetchAll();
 } catch (err) {
 console.error(err);
 alert('Failed to assign project to user: ' + (err.response?.data?.message || err.message));
 } finally {
 setAssigningProject(false);
 }
 };

 const assignToTeam = async (teamId) => {
 if (!selectedProjectName) {
 alert('Please select a project.');
 return null;
 }
 
 // Get team details to determine level and role (optional now)
 const team = teams.find(t => t.team_id === teamId);
 const teamLevel = level || team?.level || null;
 const teamRole = role || team?.role || null;
 
 try {
 console.log('📦 Assigning project to team:', { teamId, projectName: selectedProjectName, level: teamLevel, role: teamRole });
 
 const response = await axios.post(`${API}/api/admin-projects/assign-team-project`, {
 team_id: teamId,
 project_name: selectedProjectName,
 level: teamLevel,
 role: teamRole,
 create_repo: true
 }, {
 headers: getAdminHeaders()
 });
 
 console.log('✅ Project assignment response:', response.data);
 
 // The backend's assignProjectToTeam function automatically handles GitHub collaborators
 // It creates the repo and adds all team members as collaborators
 
 // Return response so caller can access repo_url
 return response.data;
 } catch (err) {
 console.error('❌ Error assigning project to team:', err);
 console.error('❌ Error response:', err.response);
 const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
 throw new Error(errorMessage);
 }
 };

 // Unassign user from project
 const unassignUser = async (userId, projectId) => {
 console.log('Unassigning user:', { userId, projectId });
 setUnassigningUser(userId);
 try {
 const url = `${API}/api/admin-projects/unassign-user/${userId}/${projectId}`;
 console.log('Making request to:', url);
 await axios.delete(url, {
 headers: getAdminHeaders()
 });
 alert('✅ User unassigned from project successfully.');
 fetchAll();
 } catch (err) {
 console.error('Failed to unassign user:', err);
 alert('❌ Failed to unassign user from project.');
 } finally {
 setUnassigningUser(null);
 setShowUnassignConfirm(null);
 }
 };

 // Delete project completely
 const deleteProject = async (projectId) => {
 setDeletingProject(projectId);
 try {
 await axios.delete(`${API}/api/admin-projects/delete-project/${projectId}`, {
 headers: getAdminHeaders()
 });
 alert('✅ Project deleted successfully.');
 fetchAll();
 } catch (err) {
 console.error('Failed to delete project:', err);
 alert('❌ Failed to delete project.');
 } finally {
 setDeletingProject(null);
 setShowDeleteConfirm(null);
 }
 };

 // View project details
 const viewProjectDetails = (user) => {
 const details = `
Project Details:
• User: ${user.name} (${user.user_id})
• Project: ${user.project_name}
• Role: ${user.role}
• Experience Level: ${user.experience_level}
• Assigned: ${new Date(user.assigned_at).toLocaleDateString()}
• Repository: ${user.repo_url || 'Not available'}
 `;
 alert(details);
 };

 // --- derive experience levels, roles & projects dynamically from project catalog AND actual enrollments
 // Show all available options from project catalog, plus actual enrolled roles from database
 // Normalization functions are defined at module level above
 
 const availableLevels = Object.keys(projectCatalog).sort();
 
 console.log('Available levels from catalog:', availableLevels);

 // Get roles from project catalog (for the selected level and tab)
 // Normalize the selected level to ensure exact match
 const normalizedSelectedLevel = normalizeLevel(level);
 
 // Get catalog roles - if level is selected, get roles for that level; otherwise get all roles from all levels
 let catalogRoles = [];
 const tabType = activeTab === 'team' ? 'team' : 'individual';
 
 console.log('🔍 Role collection debug:', {
 level,
 normalizedSelectedLevel,
 activeTab,
 tabType,
 projectCatalogKeys: Object.keys(projectCatalog || {}),
 projectCatalogExists: !!projectCatalog
 });
 
 if (normalizedSelectedLevel) {
 // When level is selected, get roles for that specific level
 const levelData = projectCatalog[normalizedSelectedLevel]?.[tabType];
 console.log('📋 Level-specific roles:', {
 levelKey: normalizedSelectedLevel,
 levelDataExists: !!levelData,
 levelDataKeys: levelData ? Object.keys(levelData) : [],
 rawRoles: levelData ? Object.keys(levelData) : []
 });
 catalogRoles = levelData ? Object.keys(levelData).map(normalizeRole).filter(Boolean) : [];
 } else {
 // When no level is selected, collect all roles from all levels in the catalog
 const allCatalogRoles = new Set();
 Object.keys(projectCatalog || {}).forEach(levelKey => {
 const levelRoles = projectCatalog[levelKey]?.[tabType];
 if (levelRoles && typeof levelRoles === 'object') {
 const roleKeys = Object.keys(levelRoles);
 console.log(`📋 Roles from level"${levelKey}":`, roleKeys);
 roleKeys.forEach(role => {
 const normalized = normalizeRole(role);
 if (normalized) {
 allCatalogRoles.add(normalized);
 }
 });
 }
 });
 catalogRoles = Array.from(allCatalogRoles);
 console.log('📋 All collected catalog roles:', Array.from(allCatalogRoles));
 }
 
 // Get roles from actual user enrollments in database
 // Normalize experience levels and roles for comparison
 const validUsers = (Array.isArray(users) ? users : []).filter(user => 
 user && 
 user.role !== null && 
 user.role !== undefined && 
 user.role !== '' &&
 user.experience_level !== null &&
 user.experience_level !== undefined &&
 user.experience_level !== ''
 ).map(user => ({
 ...user,
 normalized_experience_level: normalizeLevel(user.experience_level),
 normalized_role: normalizeRole(user.role)
 }));

 // Helper function to check if work_mode matches the active tab
 const matchesWorkMode = (user) => {
 if (activeTab === 'team') {
 return user.work_mode === 'team' || user.work_mode === 'teams';
 } else {
 // For individual tab: accept 'single', 'individual', or NULL (assuming NULL means individual for roles track)
 return user.work_mode === 'single' || 
 user.work_mode === 'individual' || 
 (user.work_mode === null && user.track === 'roles'); // NULL work_mode with roles track = individual
 }
 };

 // Get unique roles from actual enrollments that match the current tab
 const enrolledUsersForTab = validUsers.filter(user => matchesWorkMode(user));
 
 // Normalize level for comparison
 const normalizedLevel = normalizeLevel(level);
 
 // Get enrolled roles - normalize for comparison
 const enrolledRoles = normalizedLevel 
 ? [...new Set(enrolledUsersForTab
 .filter(u => normalizeLevel(u.experience_level) === normalizedLevel)
 .map(u => normalizeRole(u.role))
 )].sort()
 : [...new Set(enrolledUsersForTab.map(u => normalizeRole(u.role)))].sort();

 // Get unique experience levels from actual enrollments - normalize to match catalog
 const enrolledLevels = [...new Set(enrolledUsersForTab.map(u => normalizeLevel(u.experience_level)))].filter(Boolean).sort();

 // Combine catalog roles with enrolled roles (union, no duplicates)
 // Always include catalog roles, even when no level is selected
 // Ensure all roles are normalized for consistent matching
 const availableRoles = [...new Set([...catalogRoles, ...enrolledRoles])].sort();
 
 console.log('Available roles for level', level, 'and', activeTab, ':', {
 catalogRoles,
 enrolledRoles,
 availableRoles,
 enrolledUsersCount: enrolledUsersForTab.length
 });

 // Filter assigned users by search term (defensive in case of bad data)
 const filteredAssignedUsers = Array.isArray(assignedUsers)
 ? assignedUsers.filter(user => 
 searchTerm === '' || 
 user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 user.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 user.role?.toLowerCase().includes(searchTerm.toLowerCase())
 )
 : [];

 // Normalize level and role for project lookup
 const normalizedProjectLevel = normalizeLevel(level);
 const normalizedProjectRole = normalizeRole(role);
 
 const availableProjects = normalizedProjectRole && normalizedProjectLevel
 ? projectCatalog[normalizedProjectLevel]?.[activeTab === 'team' ? 'team' : 'individual']?.[normalizedProjectRole] || []
 : [];
 
 // Log for debugging
 if (normalizedProjectLevel && normalizedProjectRole && availableProjects.length === 0) {
 console.warn('No projects found for:', {
 level: normalizedProjectLevel,
 role: normalizedProjectRole,
 activeTab,
 availableRoles: Object.keys(projectCatalog[normalizedProjectLevel]?.[activeTab === 'team' ? 'team' : 'individual'] || {})
 });
 }

 if (loading) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-muted">Loading data...</p>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center px-4">
 <div className="text-center max-w-2xl">
 <div className="text-red-400 text-xl mb-4">⚠️ Error Loading Data</div>
 <p className="text-muted mb-4">{error}</p>
 <div className="bg-bgSection rounded-lg p-4 mb-4 text-left text-sm">
 <p className="text-primary mb-2">Debugging Steps:</p>
 <ol className="list-decimal list-inside space-y-1 text-muted">
 <li>Open Browser Console (F12) and check for error messages</li>
 <li>Check Network tab to see API request status</li>
 <li>Verify you're logged in as admin user</li>
 <li>Check if API endpoint is accessible: {API}/api/admin-teams/all-users</li>
 </ol>
 </div>
 <button 
 onClick={() => fetchAll()}
 className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary transition-colors"
 >
 Retry
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-bgMain text-body px-4 py-8">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="text-center mb-8">
 <div className="flex items-center justify-between mb-4">
 <div className="flex-1"></div>
 <h1 className="text-4xl font-bold text-primary flex items-center justify-center gap-3 flex-1">
 <ClipboardDocumentListIcon className="h-10 w-10" />
 Admin Project Team Management
 </h1>
 <div className="flex-1 flex justify-end">
 <button
 onClick={() => fetchAll()}
 disabled={loading}
 className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 title="Refresh data"
 >
 <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
 Refresh
 </button>
 </div>
 </div>
 <p className="text-muted">Manage teams and individual project assignments</p>
 
 {/* Statistics Toggle */}
 <div className="mt-4">
 <button
 onClick={() => setShowStats(!showStats)}
 className="text-sm text-muted hover:text-muted transition-colors"
 >
 {showStats ? 'Hide' : 'Show'} Statistics
 </button>
 {showStats && (
 <div className="mt-2 text-sm text-muted">
 <div className="flex flex-wrap gap-4 items-center">
 <span>Total Users from API: <span className={users.length === 0 ? 'text-red-400 font-bold' : 'text-primary'}>{users.length}</span></span>
 <span>Available Users (Filtered): <span className={filteredUsers.length === 0 ? 'text-red-400 font-bold' : 'text-primary'}>{filteredUsers.length}</span></span>
 <span>Assigned Users: <span className="text-primary">{assignedUsers.length}</span></span>
 <span>Available Levels: <span className="text-primary">{availableLevels.length}</span></span>
 <span>Available Roles: <span className="text-primary">{availableRoles.length}</span></span>
 </div>
 {users.length === 0 && (
 <div className="mt-2 p-2 bg-red-900/30 border border-red-500/50 rounded text-red-300 text-xs">
 ⚠️ No users found from API. Check browser console for details. 
 <button 
 onClick={() => fetchAll()} 
 className="ml-2 text-primary hover:text-primary underline"
 >
 Refresh
 </button>
 </div>
 )}
 {activeTab === 'individual' && enrolledUsersForTab.length > 0 && (
 <span className="block mt-1 text-primary">
 Individual Role Enrollments: {enrolledUsersForTab.length} user{enrolledUsersForTab.length !== 1 ? 's' : ''} | 
 Roles: {enrolledRoles.length > 0 ? enrolledRoles.join(', ') : 'None yet'}
 </span>
 )}
 </div>
 )}
 </div>
 </div>

 {/* Search Bar */}
 {assignedUsers.length > 0 && (
 <div className="bg-bgSection rounded-xl p-4 mb-6">
 <div className="flex items-center gap-3">
 <div className="flex-1">
 <input
 type="text"
 placeholder="Search assigned users by name, project, or role..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full bg-bgSection text-body px-4 py-2 rounded-lg border border-gray-600 focus:border-primary focus:outline-none"
 />
 </div>
 {searchTerm && (
 <button
 onClick={() => setSearchTerm('')}
 className="text-muted hover:text-white transition-colors"
 >
 <XMarkIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 </div>
 )}


 {/* Tab Navigation */}
 <div className="flex justify-center mb-6">
 <div className="bg-bgSection rounded-xl p-1 flex">
 <button
 onClick={() => setActiveTab('team')}
 className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
 activeTab === 'team' 
 ? 'bg-primary text-white font-semibold' 
 : 'text-muted hover:text-white'
 }`}
 >
 <UserGroupIcon className="h-5 w-5" />
 Team Management
 </button>
 <button
 onClick={() => setActiveTab('individual')}
 className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all ${
 activeTab === 'individual' 
 ? 'bg-primary text-white font-semibold' 
 : 'text-muted hover:text-white'
 }`}
 >
 <UserIcon className="h-5 w-5" />
 Individual Assignment
 </button>
 </div>
 </div>

 {/* Filters */}
 <div className="bg-bgSection rounded-xl p-6 mb-6">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-muted">Filter Users</h3>
 {(level || role || selectedProjectName) && (
 <button
 onClick={() => { setLevel(''); setRole(''); setSelectedProjectName(''); }}
 className="text-sm text-primary hover:text-primary transition-colors underline flex items-center gap-1"
 >
 <XMarkIcon className="h-4 w-4" />
 Clear All Filters
 </button>
 )}
 </div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <label className="block text-sm font-medium text-muted mb-2">Experience Level</label>
 <select 
 value={level} 
 onChange={(e) => { setLevel(e.target.value); setRole(''); setSelectedProjectName(''); }} 
 className="w-full bg-bgSection text-body px-3 py-2 rounded-lg border border-gray-600 focus:border-primary focus:outline-none"
 >
 <option value="">--Select Experience Level--</option>
 {(Array.isArray(availableLevels) ? availableLevels : []).map(l => <option key={l} value={l}>{l}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted mb-2">Role</label>
 <select 
 value={role} 
 onChange={(e) => { setRole(e.target.value); setSelectedProjectName(''); }} 
 className="w-full bg-bgSection text-body px-3 py-2 rounded-lg border border-gray-600 focus:border-primary focus:outline-none"
 >
 <option value="">--Select Role--</option>
 {(Array.isArray(availableRoles) ? availableRoles : []).map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted mb-2">Project</label>
 <select 
 value={selectedProjectName} 
 onChange={(e) => setSelectedProjectName(e.target.value)} 
 className="w-full bg-bgSection text-body px-3 py-2 rounded-lg border border-gray-600 focus:border-primary focus:outline-none"
 >
 <option value="">--Select Project--</option>
 {(Array.isArray(availableProjects) ? availableProjects : []).map(p => <option key={p} value={p}>{p}</option>)}
 </select>
 </div>
 </div>
 </div>

 {/* Team Management Tab */}
 {activeTab === 'team' && (
 <div className="space-y-6">
 {/* Team Creation */}
 <div className="bg-bgSection rounded-xl p-6">
 <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
 <UserGroupIcon className="h-6 w-6 text-primary" />
 Create Team
 </h3>
 
 <div className="mb-4">
 <label className="block text-sm font-medium text-muted mb-2">Team Name</label>
 <input
 type="text"
 value={teamName}
 onChange={(e) => setTeamName(e.target.value)}
 placeholder="Enter team name..."
 className="w-full bg-bgSection text-body px-3 py-2 rounded-lg border border-gray-600 focus:border-primary focus:outline-none"
 />
 </div>

 <div className="flex gap-4 mb-6">
 <button 
 onClick={() => createTeam(true)} 
 disabled={creatingTeam || !teamName.trim()}
 className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all"
 title={
 !teamName.trim() 
 ? 'Please enter a team name'
 : 'Create a random team of 4 available users (filters are optional)'
 }
 >
 {creatingTeam ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <PlusIcon className="h-5 w-5" />}
 Create Random Team
 </button>
 <button 
 onClick={() => createTeam(false)} 
 disabled={creatingTeam || !teamName.trim()}
 className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all"
 title={
 !teamName.trim() 
 ? 'Please enter a team name'
 : selectedUsers.length !== 4 
 ? `Select exactly 4 users (currently ${selectedUsers.length})` 
 : 'Create team with selected users'
 }
 >
 {creatingTeam ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <CheckCircleIcon className="h-5 w-5" />}
 Create Manual Team ({selectedUsers.length}/4)
 </button>
 </div>
 
 {selectedProjectName && (
 <div className="mb-4 p-3 bg-primary/20 border border-primary/50 rounded-lg">
 <p className="text-sm text-primary">
 💡 <strong>Project Selected:</strong> {selectedProjectName}
 <br />
 This project will be automatically assigned to the team after creation, and GitHub collaborators will be set up.
 </p>
 </div>
 )}

 {/* Available Users */}
 <div>
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-medium">Available Users ({filteredUsers.length})</h4>
 {users.length === 0 && (
 <div className="text-xs text-red-400 bg-red-900/20 px-3 py-1 rounded border border-red-500/30">
 ⚠️ No users from API - Check console
 </div>
 )}
 </div>
 {filteredUsers.length === 0 ? (
 <div className="text-center py-8 text-muted border border-gray-700 rounded-lg bg-bgSection/50">
 <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-600" />
 <p className="text-lg mb-2">
 {users.length === 0 
 ? 'No users found from API'
 : level || role
 ? `No users match the selected filters (${level || ''} ${role || ''}).`
 : activeTab === 'team'
 ? `No users available for team creation. Only users with work_mode='team' or 'teams' are shown here.`
 : `No users available for individual assignment. Only users with work_mode='single' or 'individual' are shown here.`}
 </p>
 {users.length > 0 && (
 <div className="mt-4 space-y-2">
 <p className="text-sm text-primary">
 💡 Found {users.length} user{users.length !== 1 ? 's' : ''} in database.
 {activeTab === 'team' 
 ? ` Only users with work_mode='team' or 'teams' are shown in Team Management tab.`
 : ` Only users with work_mode='single' or 'individual' are shown in Individual Assignment tab.`}
 {level || role ? ' Apply filters to narrow down further.' : ''}
 </p>
 {(level || role) && (
 <button
 onClick={() => { setLevel(''); setRole(''); setSelectedProjectName(''); }}
 className="mt-2 bg-primary hover:bg-secondary text-black px-4 py-2 rounded-lg transition-colors font-semibold"
 >
 Clear Filters & Show All {users.length} Users
 </button>
 )}
 </div>
 )}
 {users.length === 0 && (
 <button
 onClick={() => fetchAll()}
 className="mt-4 bg-primary hover:bg-secondary text-black px-4 py-2 rounded-lg transition-colors font-semibold"
 >
 Refresh & Retry
 </button>
 )}
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
 {filteredUsers.map(user => {
 const isInTeam = !!user.team_id;
 const isSelected = selectedUsers.some(u => u.user_id === user.user_id);
 return (
 <div
 key={user.user_id}
 onClick={() => !isInTeam && handleSelect(user)}
 className={`p-3 rounded-lg border-2 transition-all ${
 isInTeam
 ? 'border-gray-700 bg-bgSection/50 opacity-60 cursor-not-allowed'
 : isSelected
 ? 'border-primary bg-primary/10 cursor-pointer'
 : 'border-gray-600 bg-bgSection hover:border-gray-500 cursor-pointer'
 }`}
 title={isInTeam ? 'User is already in a team' : ''}
 >
 <div className="font-medium flex items-center gap-2">
 {user.name || user.user_id}
 {isInTeam && (
 <span className="text-xs text-primary bg-primary/20 px-2 py-0.5 rounded">
 In Team
 </span>
 )}
 </div>
 <div className="text-sm text-muted">{user.experience_level} • {user.role}</div>
 {user.track && (
 <div className="text-xs text-gray-500 mt-1">Track: {user.track} • Mode: {user.work_mode || 'N/A'}</div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>

 {/* Existing Teams */}
 <div className="bg-bgSection rounded-xl p-6">
 <h3 className="text-xl font-semibold mb-4">Existing Teams ({Array.isArray(teams) ? teams.length : 0})</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {(Array.isArray(teams) ? teams : []).map(team => (
 <div key={team.team_id} className="bg-bgSection rounded-lg p-4">
 <div className="flex justify-between items-start mb-3">
 <h4 className="font-semibold text-primary">{team.team_name}</h4>
 <span className="text-sm text-muted">{team.members?.length || 0} members</span>
 </div>
 <div className="text-sm text-muted mb-3">
 {team.level} • {team.role}
 </div>
 <div className="flex gap-2">
 <button
 onClick={() => assignToTeam(team.team_id)}
 disabled={!selectedProjectName}
 className="bg-primary hover:bg-secondary disabled:bg-gray-600 text-black px-3 py-1 rounded text-sm transition-all"
 >
 Assign Project
 </button>
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 )}

 {/* Individual Assignment Tab */}
 {activeTab === 'individual' && (
 <div className="bg-bgSection rounded-xl p-6">
 <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
 <UserIcon className="h-6 w-6 text-primary" />
 Individual Project Assignment
 </h3>
 
 {filteredUsers.length === 0 ? (
 <div className="text-center py-12 text-muted">
 <UserIcon className="h-16 w-16 mx-auto mb-4 text-gray-600" />
 <p className="text-lg mb-2">No individual role users found</p>
 <p className="text-sm">
 {level || role 
 ? `No users match the selected filters. Try selecting a different ${level ? 'role' : 'experience level'} or clear filters to see all users.`
 : 'No individual role enrollments found. Users with individual role enrollments will appear here.'}
 </p>
 {(level || role) && (
 <button
 onClick={() => { setLevel(''); setRole(''); setSelectedProjectName(''); }}
 className="mt-4 text-primary hover:text-primary transition-colors underline"
 >
 Clear Filters
 </button>
 )}
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {filteredUsers.map(user => (
 <div key={user.user_id} className="bg-bgSection rounded-lg p-4">
 <div className="font-medium mb-2">{user.name || user.user_id}</div>
 <div className="text-sm text-muted mb-3">
 {user.experience_level || 'N/A'} • {user.role || (user.track === 'roles' ? 'Individual Role' : 'N/A')}
 {user.track === 'roles' && (!user.role || !user.experience_level) && (
 <span className="ml-2 text-xs text-primary">(Roles Track)</span>
 )}
 </div>
 <button
 onClick={() => assignToUser(user.user_id)}
 disabled={!selectedProjectName || assigningProject}
 className="w-full bg-primary hover:bg-secondary disabled:bg-gray-600 text-black px-3 py-2 rounded transition-all flex items-center justify-center gap-2"
 >
 {assigningProject ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
 Assign Project
 </button>
 </div>
 ))}
 </div>
 )}
 </div>
 )}

 {/* Assigned Users Section */}
 {assignedUsers.length > 0 && (
 <div className="bg-bgSection rounded-xl p-6 mt-6">
 <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
 <CheckCircleIcon className="h-6 w-6 text-green-400" />
 Already Assigned Users ({filteredAssignedUsers.length} of {assignedUsers.length})
 </h3>
 
 {filteredAssignedUsers.length === 0 && searchTerm ? (
 <div className="text-center py-8 text-muted">
 <p>No assigned users found matching"{searchTerm}"</p>
 <button
 onClick={() => setSearchTerm('')}
 className="mt-2 text-primary hover:text-primary transition-colors"
 >
 Clear search
 </button>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {filteredAssignedUsers.map((user, index) => (
 <div key={`${user.user_id}-${user.project_id}-${index}`} className="bg-bgSection rounded-lg p-4 border-l-4 border-green-400">
 <div className="flex justify-between items-start mb-2">
 <div className="font-medium">{user.name || user.user_id}</div>
 <div className="flex gap-1">
 <button
 onClick={() => viewProjectDetails(user)}
 className="p-1 text-blue-400 hover:text-blue-300 transition-colors"
 title="View Details"
 >
 <EyeIcon className="h-4 w-4" />
 </button>
 </div>
 </div>
 <div className="text-sm text-muted mb-2">{user.experience_level} • {user.role}</div>
 <div className="text-sm text-green-400 mb-2">
 <strong>Project:</strong> {user.project_name}
 </div>
 {user.repo_url && (
 <div className="text-xs text-blue-400 mb-2">
 <strong>Repo:</strong> <a href={user.repo_url} target="_blank" rel="noopener noreferrer" className="underline">View Repository</a>
 </div>
 )}
 <div className="text-xs text-gray-500 mb-3">
 Assigned: {new Date(user.assigned_at).toLocaleDateString()}
 </div>

 {/* Action Buttons */}
 <div className="flex gap-2">
 {user.team_id && (
 <button
 onClick={() => window.open(`/team-roles`, '_blank')}
 className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded text-sm transition-all flex items-center justify-center gap-1"
 title="Open Team Workspace"
 >
 <UserGroupIcon className="h-3 w-3" />
 Team Workspace
 </button>
 )}
 <button
 onClick={() => {
 console.log('Setting unassign confirm for user:', user);
 setShowUnassignConfirm(user);
 }}
 disabled={unassigningUser === user.user_id}
 className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 text-white px-3 py-2 rounded text-sm transition-all flex items-center justify-center gap-1"
 >
 {unassigningUser === user.user_id ? (
 <ArrowPathIcon className="h-3 w-3 animate-spin" />
 ) : (
 <XMarkIcon className="h-3 w-3" />
 )}
 Unassign
 </button>
 <button
 onClick={() => {
 console.log('Setting delete confirm for user:', user);
 setShowDeleteConfirm(user);
 }}
 disabled={deletingProject === user.project_id}
 className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-600 text-white px-3 py-2 rounded text-sm transition-all flex items-center justify-center gap-1"
 >
 {deletingProject === user.project_id ? (
 <ArrowPathIcon className="h-3 w-3 animate-spin" />
 ) : (
 <TrashIcon className="h-3 w-3" />
 )}
 Delete
 </button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 )}

 {/* Confirmation Modals */}
 {/* Unassign Confirmation Modal */}
 {showUnassignConfirm && (
 <div className="fixed inset-0 bg-bgMain bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-bgSection rounded-xl p-6 max-w-md w-full mx-4">
 <div className="flex items-center gap-3 mb-4">
 <ExclamationTriangleIcon className="h-8 w-8 text-orange-400" />
 <h3 className="text-xl font-semibold text-white">Unassign User</h3>
 </div>
 <p className="text-muted mb-6">
 Are you sure you want to unassign <strong>{showUnassignConfirm.name}</strong> from the project <strong>{showUnassignConfirm.project_name}</strong>?
 <br /><br />
 <strong className="text-orange-400">WARNING:</strong> This will remove the project assignment AND delete the entire project from the system. This action cannot be undone.
 </p>
 <div className="flex gap-3">
 <button
 onClick={() => setShowUnassignConfirm(null)}
 className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
 >
 Cancel
 </button>
 <button
 onClick={() => unassignUser(showUnassignConfirm.user_id, showUnassignConfirm.project_id)}
 className="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded transition-colors"
 >
 Unassign
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Delete Confirmation Modal */}
 {showDeleteConfirm && (
 <div className="fixed inset-0 bg-bgMain bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-bgSection rounded-xl p-6 max-w-md w-full mx-4">
 <div className="flex items-center gap-3 mb-4">
 <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
 <h3 className="text-xl font-semibold text-white">Delete Project</h3>
 </div>
 <p className="text-muted mb-6">
 <strong className="text-red-400">WARNING:</strong> This will permanently delete the project <strong>{showDeleteConfirm.project_name}</strong> and all associated data.
 <br /><br />
 This action cannot be undone. The user will be unassigned and the project will be completely removed.
 </p>
 <div className="flex gap-3">
 <button
 onClick={() => setShowDeleteConfirm(null)}
 className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
 >
 Cancel
 </button>
 <button
 onClick={() => deleteProject(showDeleteConfirm.project_id)}
 className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors"
 >
 Delete Project
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default AdminProjectTeam;



